import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from '../Config/firebase';

// Importações do Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faSpinner } from '@fortawesome/free-solid-svg-icons';

import './novaconta.css';

const UFs = [
    { uf: 'AC', nome: 'Acre' }, { uf: 'AL', nome: 'Alagoas' }, { uf: 'AP', nome: 'Amapá' },
    { uf: 'AM', nome: 'Amazonas' }, { uf: 'BA', nome: 'Bahia' }, { uf: 'CE', nome: 'Ceará' },
    { uf: 'DF', nome: 'Distrito Federal' }, { uf: 'ES', nome: 'Espírito Santo' }, { uf: 'GO', nome: 'Goiás' },
    { uf: 'MA', nome: 'Maranhão' }, { uf: 'MT', nome: 'Mato Grosso' }, { uf: 'MS', nome: 'Mato Grosso do Sul' },
    { uf: 'MG', nome: 'Minas Gerais' }, { uf: 'PA', nome: 'Pará' }, { uf: 'PB', nome: 'Paraíba' },
    { uf: 'PR', nome: 'Paraná' }, { uf: 'PE', nome: 'Pernambuco' }, { uf: 'PI', nome: 'Piauí' },
    { uf: 'RJ', nome: 'Rio de Janeiro' }, { uf: 'RN', nome: 'Rio Grande do Norte' }, { uf: 'RS', nome: 'Rio Grande do Sul' },
    { uf: 'RO', nome: 'Rondônia' }, { uf: 'RR', nome: 'Roraima' }, { uf: 'SC', nome: 'Santa Catarina' },
    { uf: 'SP', nome: 'São Paulo' }, { uf: 'SE', nome: 'Sergipe' }, { uf: 'TO', nome: 'Tocantins' }
];

function NovaConta() {
    const [tipoPessoa, setTipoPessoa] = useState('fisica');
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [confirmaSenha, setConfirmaSenha] = useState('');
    const [nome, setNome] = useState('');
    const [cpf, setCpf] = useState('');
    const [cnpj, setCnpj] = useState('');
    const [razaoSocial, setRazaoSocial] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [cep, setCep] = useState('');
    const [estado, setEstado] = useState('');
    const [cidade, setCidade] = useState('');
    const [modulosSelecionados, setModulosSelecionados] = useState([1]); // 1 = Essencial (Sempre marcado)
    const [licencas, setLicencas] = useState(1);
    const [cidades, setCidades] = useState([]);
    const [mensagem, setMensagem] = useState('');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingCEP, setLoadingCEP] = useState(false);
    const [showSenha, setShowSenha] = useState(false);
    const [showConfirmaSenha, setShowConfirmaSenha] = useState(false);

    // Calculate subscription value based on plan and licenses
    const valorAssinatura = useMemo(() => {
        let total = 89.90; // Base Essencial
        if (modulosSelecionados.includes(2)) total += 30.00; // Estoque
        if (modulosSelecionados.includes(3)) total += 40.00; // Produção

        // Licenças adicionais (R$ 29,90 cada extra)
        if (licencas > 1) {
            total += (licencas - 1) * 29.90;
        }
        return total;
    }, [licencas, modulosSelecionados]);

    const handleModuloChange = (id) => {
        if (id === 1) return; // Essencial não pode mudar

        setModulosSelecionados(prev => {
            let novos = [...prev];

            if (novos.includes(id)) {
                // Desmarcando
                novos = novos.filter(m => m !== id);
                // Se desmarcar Estoque (2), e Produção (3) estiver marcado, desmarca Produção também?
                // O usuário disse: "Ao marcar o modulo produção automaticamente deve marcar o modulo estoque e não permitir desmarcar."
                // Então se eu desmarcar Estoque, devo desmarcar Produção também logicamente, pois Produção exige Estoque.
                if (id === 2 && novos.includes(3)) {
                    novos = novos.filter(m => m !== 3);
                }
            } else {
                // Marcando
                novos.push(id);
                // Se marcar Produção (3), força Estoque (2)
                if (id === 3 && !novos.includes(2)) {
                    novos.push(2);
                }
            }
            return novos;
        });
    };

    const buscarCidadesPorUF = useCallback(async (uf) => {
        if (!uf) {
            setCidades([]);
            return null;
        }
        try {
            const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`);
            if (!response.ok) throw new Error('Falha na resposta da API de cidades');
            const data = await response.json();
            const listaDeCidades = data.map(municipio => municipio.nome).sort();
            setCidades(listaDeCidades);
            return listaDeCidades;
        } catch (error) {
            console.error("Erro ao buscar cidades:", error);
            setMensagem('Não foi possível carregar as cidades.');
            setCidades([]);
            return null;
        }
    }, []);

    const buscarCEP = async (cepValue) => {
        const cepLimpo = cepValue.replace(/\D/g, '');
        setCep(cepLimpo);
        if (cepLimpo.length !== 8) return;
        setLoadingCEP(true);
        setMensagem('');
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
            if (!response.ok) throw new Error('Falha na resposta da API de CEP');
            const data = await response.json();
            if (data.erro) {
                setMensagem('CEP não encontrado.');
                setEstado('');
                setCidade('');
                setCidades([]);
            } else {
                setEstado(data.uf);
                const cidadesDoEstado = await buscarCidadesPorUF(data.uf);
                if (cidadesDoEstado && cidadesDoEstado.includes(data.localidade)) {
                    setCidade(data.localidade);
                }
            }
        } catch (error) {
            console.error("Erro ao buscar CEP:", error);
            setMensagem('Erro ao consultar o CEP.');
        } finally {
            setLoadingCEP(false);
        }
    };

    useEffect(() => {
        if (estado) {
            buscarCidadesPorUF(estado);
        }
    }, [estado, buscarCidadesPorUF]);

    const formatarWhatsapp = (value) => {
        const numero = value.replace(/\D/g, '').substring(0, 11);
        let numeroFormatado = '';
        if (numero.length > 2) {
            numeroFormatado = `(${numero.substring(0, 2)})`;
            if (numero.length > 7) {
                numeroFormatado += ` ${numero.substring(2, 7)}-${numero.substring(7)}`;
            } else {
                numeroFormatado += ` ${numero.substring(2)}`;
            }
        } else {
            numeroFormatado = numero;
        }
        setWhatsapp(numeroFormatado);
    };

    const validarFormulario = () => {
        if (!email || !senha || !confirmaSenha || !whatsapp || !cidade || !estado || !cep) { // <-- REMOVIDO !plano
            setMensagem('Por favor, preencha todos os campos obrigatórios.');
            return false;
        }
        if (tipoPessoa === 'fisica' && (!nome || !cpf)) {
            setMensagem('Por favor, preencha o Nome e CPF.');
            return false;
        }
        if (tipoPessoa === 'juridica' && (!cnpj || !razaoSocial)) {
            setMensagem('Por favor, preencha o CNPJ e a Razão Social.');
            return false;
        }
        if (senha !== confirmaSenha) {
            setMensagem('A senha e a confirmação de senha não são iguais.');
            return false;
        }
        return true;
    }

    async function cadastrarUsuario(e) {
        e.preventDefault();
        setMensagem('');
        if (!validarFormulario()) return;
        setLoading(true);

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, senha);
            const firebaseUser = userCredential.user;

            await signOut(auth);

            await signOut(auth);

            const dataVencimento = new Date();
            dataVencimento.setDate(dataVencimento.getDate() + 7);

            const payload = {
                email: firebaseUser.email,
                firebaseUid: firebaseUser.uid,
                nome: tipoPessoa === 'fisica' ? nome : razaoSocial,
                nomeEmpresa: tipoPessoa === 'fisica' ? nome : razaoSocial,
                cpfCnpj: tipoPessoa === 'fisica' ? cpf : cnpj,
                telefone: whatsapp.replace(/\D/g, ''),
                cep: cep,
                uf: estado,
                cidade: cidade,
                status: 2, // Sempre "Em teste"
                dataVencimento: dataVencimento.toISOString().split('T')[0],
                max_sessions: licencas,
                valor: valorAssinatura,
                modulos: modulosSelecionados // [NOVO] IDs dos módulos
            };

            // ======================= INÍCIO DA CORREÇÃO =======================
            const API_URL = process.env.REACT_APP_API_URL;
            const response = await fetch(`${API_URL}/api/register`, {
                // ======================== FIM DA CORREÇÃO =========================
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Falha ao registrar os dados no servidor.');
            }

            navigate('/app', { state: { email: email, message: 'Conta criada com sucesso! Por favor, faça o login.' } });

        } catch (error) {
            if (error.code === 'auth/email-already-in-use') {
                setMensagem('Já existe uma conta cadastrada com este e-mail.');
            } else if (error.code === 'auth/weak-password') {
                setMensagem('Sua senha deve ter no mínimo 6 caracteres.');
            } else {
                setMensagem(error.message || 'Ocorreu um erro ao criar a conta. Tente novamente.');
                console.error('Erro ao criar conta:', error);
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="d-flex justify-content-center align-items-start bg-light min-vh-100 py-5">
            <div className="container">
                <div className="row g-5 justify-content-center">

                    {/* Coluna do Formulário */}
                    <div className="col-md-7 col-lg-6">
                        <div className="card shadow-sm border-0">
                            <div className="card-body p-4 p-md-5">
                                <form className="form-signin text-center" onSubmit={cadastrarUsuario}>
                                    <img className="mb-4" src="/imagens/logo_fundo_branco_pequena.png" alt="Logo R6 ERP" height="60" />
                                    <h1 className="h3 mb-4 fw-normal text-start">Criar Conta</h1>

                                    {/* Tipo Pessoa */}
                                    <div className="btn-group mb-4 w-100" role="group">
                                        <input type="radio" className="btn-check" name="tipoPessoa" id="fisica" autoComplete="off" checked={tipoPessoa === 'fisica'} onChange={() => setTipoPessoa('fisica')} />
                                        <label className="btn btn-outline-primary-custom" htmlFor="fisica">Pessoa Física</label>
                                        <input type="radio" className="btn-check" name="tipoPessoa" id="juridica" autoComplete="off" checked={tipoPessoa === 'juridica'} onChange={() => setTipoPessoa('juridica')} />
                                        <label className="btn btn-outline-primary-custom" htmlFor="juridica">Pessoa Jurídica</label>
                                    </div>

                                    {/* Campos do Formulário */}
                                    {tipoPessoa === 'fisica' ? (
                                        <>
                                            <div className="form-floating mb-3 text-start">
                                                <input onChange={(e) => setNome(e.target.value)} type="text" className="form-control" id="nomeInput" placeholder="Seu nome completo" required />
                                                <label htmlFor="nomeInput">Nome Completo</label>
                                            </div>
                                            <div className="form-floating mb-3 text-start">
                                                <input onChange={(e) => setCpf(e.target.value)} type="text" className="form-control" id="cpfInput" placeholder="000.000.000-00" maxLength="11" required />
                                                <label htmlFor="cpfInput">CPF</label>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="form-floating mb-3 text-start">
                                                <input onChange={(e) => setRazaoSocial(e.target.value)} type="text" className="form-control" id="razaoSocialInput" placeholder="Nome da sua empresa" required />
                                                <label htmlFor="razaoSocialInput">Razão Social</label>
                                            </div>
                                            <div className="form-floating mb-3 text-start">
                                                <input onChange={(e) => setCnpj(e.target.value)} type="text" className="form-control" id="cnpjInput" placeholder="00.000.000/0000-00" maxLength="14" required />
                                                <label htmlFor="cnpjInput">CNPJ</label>
                                            </div>
                                        </>
                                    )}

                                    <div className="form-floating mb-3 text-start">
                                        <input onChange={(e) => setEmail(e.target.value)} type="email" className="form-control" id="emailInput" placeholder="seu@email.com" required />
                                        <label htmlFor="emailInput">E-mail</label>
                                    </div>

                                    <div className="form-floating mb-3 text-start">
                                        <input onChange={(e) => formatarWhatsapp(e.target.value)} type="tel" className="form-control" id="whatsappInput" placeholder="(99) 99999-9999" value={whatsapp} required />
                                        <label htmlFor="whatsappInput">WhatsApp</label>
                                    </div>

                                    <div className="row g-2">
                                        <div className="col-md-5">
                                            <div className="form-floating mb-3 text-start">
                                                <input onChange={(e) => buscarCEP(e.target.value)} type="text" className="form-control" id="cepInput" placeholder="00000-000" value={cep} maxLength="8" required />
                                                <label htmlFor="cepInput">
                                                    CEP {loadingCEP && <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>}
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-md-7">
                                            <div className="form-floating mb-3 text-start">
                                                <select className="form-select" id="estadoInput" value={estado} onChange={(e) => setEstado(e.target.value)} required>
                                                    <option value="">Selecione</option>
                                                    {UFs.map((uf) => (<option key={uf.uf} value={uf.uf}>{uf.nome}</option>))}
                                                </select>
                                                <label htmlFor="estadoInput">Estado</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-floating mb-3 text-start">
                                        <select className="form-select" id="cidadeInput" value={cidade} onChange={(e) => setCidade(e.target.value)} required disabled={cidades.length === 0}>
                                            <option value="">{estado ? 'Selecione uma cidade' : 'Selecione o estado primeiro'}</option>
                                            {cidades.map((cidadeNome) => (<option key={cidadeNome} value={cidadeNome}>{cidadeNome}</option>))}
                                        </select>
                                        <label htmlFor="cidadeInput">Cidade</label>
                                    </div>

                                    <div className="form-floating mb-3 text-start position-relative">
                                        <input onChange={(e) => setSenha(e.target.value)} type={showSenha ? 'text' : 'password'} className="form-control" id="passwordInput" placeholder="Crie uma senha forte" required />
                                        <label htmlFor="passwordInput">Senha</label>
                                        <button type="button" className="btn btn-password-toggle-floating" onClick={() => setShowSenha(!showSenha)} style={{
                                            position: 'absolute', right: '10px', top: '15px', background: 'none', border: 'none', zIndex: 10
                                        }}>
                                            <FontAwesomeIcon icon={showSenha ? faEyeSlash : faEye} className="text-muted" />
                                        </button>
                                    </div>

                                    <div className="form-floating mb-3 text-start position-relative">
                                        <input onChange={(e) => setConfirmaSenha(e.target.value)} type={showConfirmaSenha ? 'text' : 'password'} className="form-control" id="confirmPasswordInput" placeholder="Repita a senha" required />
                                        <label htmlFor="confirmPasswordInput">Confirme sua senha</label>
                                        <button type="button" className="btn btn-password-toggle-floating" onClick={() => setShowConfirmaSenha(!showConfirmaSenha)} style={{
                                            position: 'absolute', right: '10px', top: '15px', background: 'none', border: 'none', zIndex: 10
                                        }}>
                                            <FontAwesomeIcon icon={showConfirmaSenha ? faEyeSlash : faEye} className="text-muted" />
                                        </button>
                                    </div>

                                    <button className="btn btn-primary w-100 py-3 fw-bold mt-2" type="submit" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                                Criando conta...
                                            </>
                                        ) : (
                                            'CRIAR CONTA'
                                        )}
                                    </button>

                                    {mensagem && <div className={`alert alert-${mensagem.includes('sucesso') ? 'success' : 'danger'} mt-3`} role="alert">{mensagem}</div>}

                                    <div className="login-links mt-4">
                                        <Link to="/app" className="mx-3 text-decoration-none">Já tenho uma conta</Link>
                                    </div>
                                    <p className="mt-5 mb-0 text-muted small">&copy; R6 Software</p>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Coluna de Seleção de Planos (Lateral) */}
                    <div className="col-md-5 col-lg-5 order-first order-md-last">
                        <div className="card shadow-sm border-0 sticky-top" style={{ top: '20px', zIndex: 1 }}>
                            <div className="card-header bg-primary text-white text-center py-3">
                                <h5 className="mb-0 fw-bold">Personalize seu Plano</h5>
                            </div>
                            <div className="card-body p-4">

                                <div className="mb-4">
                                    <label className="form-label fw-bold">Quantidade de Licenças</label>
                                    <div className="input-group">
                                        <button className="btn btn-outline-secondary" type="button" onClick={() => setLicencas(l => Math.max(1, l - 1))}><i className="fas fa-minus"></i></button>
                                        <input type="text" className="form-control text-center" value={licencas} readOnly />
                                        <button className="btn btn-outline-secondary" type="button" onClick={() => setLicencas(l => l + 1)}><i className="fas fa-plus"></i></button>
                                    </div>
                                    <div className="form-text mt-2">
                                        Incluso 1 usuário. Adicionais: <strong>R$ 29,90/usuário</strong>
                                    </div>
                                </div>

                                <hr className="my-4" />

                                <h6 className="fw-bold mb-3">Módulos Disponíveis</h6>

                                <div className="list-group mb-4">
                                    {/* Módulo Essencial */}
                                    <label className="list-group-item d-flex justify-content-between align-items-center bg-light">
                                        <div className="d-flex align-items-center">
                                            <input className="form-check-input me-3" type="checkbox" checked={true} disabled />
                                            <div>
                                                <span className="fw-bold d-block">Módulo Essencial</span>
                                                <small className="text-muted d-block mb-1">Recursos básicos do sistema:</small>
                                                <ul className="mb-0 ps-3 small text-muted">
                                                    <li>Cadastro de empresas e produtos</li>
                                                    <li>Emissão de pedidos</li>
                                                    <li>Financeiro a receber e pagar</li>
                                                    <li>Dashboard de vendas e financeiro</li>
                                                </ul>
                                            </div>
                                        </div>
                                        <span className="fw-bold text-primary">R$ 89,90</span>
                                    </label>

                                    {/* Módulo Estoque */}
                                    <label className={`list-group-item d-flex justify-content-between align-items-center ${modulosSelecionados.includes(3) ? 'bg-light' : ''}`} style={{ cursor: modulosSelecionados.includes(3) ? 'not-allowed' : 'pointer' }}>
                                        <div className="d-flex align-items-center">
                                            <input
                                                className="form-check-input me-3"
                                                type="checkbox"
                                                checked={modulosSelecionados.includes(2)}
                                                onChange={() => handleModuloChange(2)}
                                                disabled={modulosSelecionados.includes(3)} // Bloqueado se Produção estiver marcado
                                            />
                                            <div>
                                                <span className="fw-bold d-block">Controle de Estoque</span>
                                                <small className="text-muted d-block mb-1">Gestão avançada de estoque:</small>
                                                <ul className="mb-0 ps-3 small text-muted">
                                                    <li>Controle de estoque</li>
                                                    <li>Movimentações de estoque</li>
                                                </ul>
                                            </div>
                                        </div>
                                        <span className="fw-bold text-success">+ R$ 30,00</span>
                                    </label>

                                    {/* Módulo Produção */}
                                    <label className="list-group-item d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }}>
                                        <div className="d-flex align-items-center">
                                            <input
                                                className="form-check-input me-3"
                                                type="checkbox"
                                                checked={modulosSelecionados.includes(3)}
                                                onChange={() => handleModuloChange(3)}
                                            />
                                            <div>
                                                <span className="fw-bold d-block">Produção</span>
                                                <small className="text-muted d-block mb-1">Controle de ordens e etapas:</small>
                                                <ul className="mb-0 ps-3 small text-muted">
                                                    <li>Ficha técnica e Produção</li>
                                                    <li>Controle de insumos</li>
                                                </ul>
                                                {modulosSelecionados.includes(3) && <span className="badge bg-info text-dark mt-2">Inclui Estoque</span>}
                                            </div>
                                        </div>
                                        <span className="fw-bold text-success">+ R$ 40,00</span>
                                    </label>
                                </div>

                                <div className="total-box bg-light p-3 rounded text-center border">
                                    <small className="text-uppercase text-muted fw-bold ls-1">Valor Mensal</small>
                                    <div className="display-6 fw-bold text-primary my-1">
                                        {valorAssinatura.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </div>
                                    <small className="text-muted">Cobrado mensalmente</small>
                                </div>
                                <div className="trial-notice mt-3 text-center text-success fw-bold">
                                    <i className="fas fa-gift me-2"></i> 7 dias de teste grátis!
                                </div>

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default NovaConta;